import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { routePaths } from "../../../../common/infrastructure/routes/routePaths";
import { useAppContext } from "../../../../AppContext";
import { showCustomConfirmDialog } from "../../../../common/components/dialogs/AppConfirmDialog";
import { IInventoryTransferListItemModel, IInventoryTransferLineFormModel } from "../models/inventoryTransferModels";
import { UnitOfMeasure } from "../../../../common/enums";
import { useTransferDialogPermissions } from "./useTransferDialogPermissions";

/**
 * Page-level logic for the Доставки list:
 *  - fetches all transfers for the current studio
 *  - owns the create/edit dialog open state
 *  - exposes `requestDelete` which opens the global AppConfirmDialog and,
 *    on accept, fires the toggle-deletion mutation.
 */
export const useInventoryTransfersPage = () => {
  const { selectedStudioId } = useAppContext();
  const endpoints = endpointsMetadata.inventoryTransfers;
  const queryClient = useQueryClient();
  const { canDelete } = useTransferDialogPermissions();
  const location = useLocation();
  const navigate = useNavigate();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTransferId, setEditingTransferId] = useState<number | null>(null);
  const [prefillLines, setPrefillLines] = useState<IInventoryTransferLineFormModel[] | null>(null);

  // Auto-open dialog with pre-filled lines when navigated from the overview page.
  useEffect(() => {
    const state = location.state as { lowStockItems?: Array<{ inventoryItemId: number; productName: string; categoryEmoji: string | null }> } | null;
    if (state?.lowStockItems && state.lowStockItems.length > 0) {
      const lines: IInventoryTransferLineFormModel[] = state.lowStockItems.map((item) => ({
        id: null,
        inventoryItemId: item.inventoryItemId,
        quantity: 1,
        expirationDate: null,
        productName: item.productName,
        emoji: item.categoryEmoji,
        unitOfMeasure: UnitOfMeasure.Piece,
      }));
      setPrefillLines(lines);
      setEditingTransferId(null);
      setIsDialogOpen(true);
      // Clear the state so refreshing doesn't re-trigger.
      navigate(routePaths.inventoryTransfers, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  // Managers+ can see deleted transfers; instructors use the basic endpoint.
  const listEndpoint = canDelete ? endpoints.getAllWithDeleted : endpoints.getAll;
  const listQueryKey = [listEndpoint, selectedStudioId];

  const { data: transfers = [], isLoading } = useApiQuery<IInventoryTransferListItemModel[]>({
    queryKey: listQueryKey,
    endpoint: listEndpoint,
    enabled: selectedStudioId > 0,
    globalIsLoading: false,
  });

  const toggleDeletionMutation = useApiMutation<string>({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listQueryKey });
      // Also invalidate the product list because restoring/removing a Completed
      // transfer applies or reverses stock on the affected products.
      queryClient.invalidateQueries({
        queryKey: [endpointsMetadata.inventoryItemNomenclature.getAllCategorized, selectedStudioId],
      });
    },
  });

  const openCreate = useCallback(() => {
    setEditingTransferId(null);
    setIsDialogOpen(true);
  }, []);

  const openEdit = useCallback((transferId: number) => {
    setEditingTransferId(transferId);
    setIsDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
    setEditingTransferId(null);
    setPrefillLines(null);
  }, []);

  const requestDelete = useCallback(
    (transfer: IInventoryTransferListItemModel) => {
      const message = transfer.isDeleted
        ? `Възстановяване на доставка <b>${transfer.displayNumber}</b>?<br/>Ако е била завършена, количествата ще бъдат приложени отново към продуктите.`
        : `Изтриване на доставка <b>${transfer.displayNumber}</b>?<br/>Ако е завършена, количествата ще бъдат възстановени.`;

      showCustomConfirmDialog(
        message,
        () => {
          toggleDeletionMutation.mutate({
            endpoint: endpoints.toggleDeletionStatus,
            method: "put",
            additionalParams: `itemId=${transfer.id}`,
          });
        },
        undefined,
        "warning",
        transfer.isDeleted ? "Възстанови" : "Изтрий",
        "Отказ",
      );
    },
    [endpoints.toggleDeletionStatus, toggleDeletionMutation],
  );

  return {
    endpoints,
    transfers,
    isLoading,
    isDialogOpen,
    editingTransferId,
    prefillLines,
    openCreate,
    openEdit,
    closeDialog,
    requestDelete,
    isMutating: toggleDeletionMutation.isLoading,
  };
};
